import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Button } from "@shared/ui/Button";
import { Avatar, Flex, Rate, Space } from "antd";
import { useEffect } from "react";

import { ToastService } from "@shared/lib/services";
import {
  IBrandCostRequest,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import styles from "./styles.module.scss";

import { useAddBrandCost } from "@entities/brief/lib/hooks/useAddBrandCost";
import { useAddInitialBrandCost } from "@entities/brief/lib/hooks/useAddInitialBrandCost";
import { useEditBrandCost } from "@entities/brief/lib/hooks/useEditBrandCost";
import { getInfoDetails } from "@entities/influencer/lib/utils/getInfoDetails";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { AddCommercials } from "./AddCommercials";
import { Deliverables } from "./Deliverables";

interface BrandCommercialProps {
  briefInfluencer: IBriefInfluencerV2;
  onClose: () => void;
  type: "view" | "create-new" | "create";
}

export function BrandCommercial({
  briefInfluencer,
  onClose,
  type,
}: BrandCommercialProps) {
  const isViewOnly = type === "view";

  const { briefId, brief } = useBriefStore();

  const creatorInfo = getInfoDetails(briefInfluencer);

  const { addInitialCost, isAdded, isAdding } = useAddInitialBrandCost(briefId);
  const {
    addCost,
    isAdded: isAddedCost,
    isAdding: isAddingCost,
  } = useAddBrandCost(briefId);

  const { editCost, isEditing, isEdited } = useEditBrandCost(briefId);

  const handleSaveCommercials = (
    values: Omit<IBrandCostRequest, "briefInfluencerId">
  ) => {
    if (isViewOnly) {
      return editCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
      });
    }
    if (type === "create-new") {
      addCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
        isNonCancellable: values?.isNonCancellable || false,
      });
    } else {
      addInitialCost({
        ...values,
        briefInfluencerId: briefInfluencer._id,
        isNonCancellable: values?.isNonCancellable || false,
      });
    }
  };

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Initial cost added successfully");
      onClose();
    }
    if (isAddedCost) {
      ToastService.success("Cost added successfully");
      onClose();
    }
    if (isEdited) {
      ToastService.success("Cost updated successfully");
      onClose();
    }
  }, [isAdded, isAddedCost, isEdited]);

  return (
    <Flex vertical gap={24}>
      <div className={styles.infoCard}>
        <Flex justify="space-between">
          <div className={styles.profileSection}>
            <Avatar src={creatorInfo.dp} className={styles.avatar} size={72} />
            <div>
              <Space align="start">
                <div className={styles.name}>{creatorInfo.name}</div>
                {creatorInfo.isVerified && (
                  <img src="/instaTick.svg" alt="verified" />
                )}
              </Space>

              <Button
                type="link"
                size="small"
                style={{ display: "block", padding: "0" }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(creatorInfo?.link, "_blank");
                }}
              >
                <Space style={{ color: "#888", fontSize: 12 }} size={2}>
                  {getPlatformIcon(creatorInfo?.platform || "")}
                  {creatorInfo?.username}
                </Space>
              </Button>

              <div className={styles.rating}>
                <Rate disabled value={creatorInfo.rating} />
              </div>
            </div>
          </div>
        </Flex>
      </div>

      <div className={styles.infoCard} style={{ padding: 20 }}>
        <Deliverables
          deliverable={briefInfluencer?.deliverableV2}
          briefId={briefId}
          brief={brief}
        />
      </div>
      <div className={styles.infoCard} style={{ padding: 20 }}>
        <AddCommercials
          initialValues={{
            cost: briefInfluencer.brandCost?.cost,
            estViews: briefInfluencer?.estViews,
            note: briefInfluencer?.brandCost?.note,
            isNonCancellable: briefInfluencer?.isNonCancellable || false,
          }}
          infNotes={briefInfluencer?.influencerCost?.note || ""}
          influencerPrice={briefInfluencer?.influencerCost?.cost || 0}
          onClose={onClose}
          isLoading={isAdding || isAddingCost || isEditing}
          handleSave={handleSaveCommercials}
          briefInfluencer={briefInfluencer}
        />
      </div>
    </Flex>
  );
}
